import { api, Api } from '@/services/api';
import { getLogtoRolesFromAccessToken } from '@/lib/logto';
import type { AuthUser } from '@/store/auth.slice';
import type { User } from '@/types';

const authApi = api.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Fetch the QLASH user record for the currently authenticated Logto
     * subject. The Laravel API serializes the user via `ShortUserResource`,
     * which uses field names that don't match our AuthUser shape — we map
     * here so the slice and downstream UI can use a single canonical shape.
     *
     * Specifically `avatar` (full S3 URL) becomes `avatarUrl`, and `nickname`
     * lands in `username` so mobile-app's `meData?.avatar` / `meData?.nickname`
     * have direct equivalents in our store.
     */
    getMe: builder.query<AuthUser, void>({
      query: () => `${Api.Users}/me`,
      transformResponse: (raw: User): AuthUser => {
        // Laravel `/users/me` doesn't expose `is_admin` (gated behind the
        // `X-ADMIN` backoffice header) and has no `beta_tester` column at
        // all. Derive both flags from the Logto access token's `roles`
        // claim instead — Logto's PROD tenant injects that claim via the
        // Custom JWT script. The fields stay on AuthUser so existing
        // consumers (Hub badges, feature flags, etc.) can read them by
        // name; only their source changes.
        const logtoRoles = getLogtoRolesFromAccessToken();
        return {
          id: raw.id,
          username: raw.nickname || undefined,
          firstName: raw.name || undefined,
          email: raw.email || undefined,
          avatarUrl: raw.avatar ?? undefined,
          is_admin: logtoRoles.includes('administrator'),
          beta_tester: logtoRoles.includes('beta'),
        };
      },
      providesTags: ['Users'],
    }),
    /**
     * Optional: most server-side state lives in JWT claims, so a Laravel
     * logout call is not strictly required — Logto `signOut()` clears the
     * client tokens. Kept here for parity in case the backend grows
     * server-side session tracking later.
     */
    logout: builder.mutation<void, void>({
      query: () => ({ url: Api.Logout, method: 'POST' }),
    }),
  }),
});

export const { useGetMeQuery, useLogoutMutation } = authApi;
